import type { SupabaseClient } from "@supabase/supabase-js";

// Platform support sessions — "act as this member" (product lock 2026-08-04, S1a…S6a).
// Thin wire ONLY over Codex's three hosted RPCs; there is no table read, no DML fallback, and
// nothing here decides who may act as whom. The server is the authority on every question that
// matters: who may start (platform superadmin), which membership is a legal target (active, on
// the account), how long a session may run, and what the actor can see while it is open. This
// module's whole job is to ask honestly and refuse to invent a success.
//
// Error doctrine matches member-lifecycle.ts: contract-stable, value-free sentences from the
// migration render VERBATIM; anything else fails closed to a safe generic so a raw Postgres
// error can never reach an operator's screen.
//
// NOTE: KNOWN_SUPPORT_SESSION_FAILURES matches RAISE messages from migration
// 20260804120000_platform_support_sessions.sql. Unmatched messages fail closed to
// the safe generic so raw Postgres errors never reach the operator.

export const SUPPORT_SESSION_DEFAULT_TTL_MINUTES = 30;
export const SUPPORT_SESSION_MIN_TTL_MINUTES = 1;
export const SUPPORT_SESSION_MAX_TTL_MINUTES = 60;
/** Server enforces the real minimum; the adapter refuses obviously-empty reasons before the wire. */
export const SUPPORT_SESSION_MIN_REASON_LENGTH = 8;

export const SAFE_SUPPORT_SESSION_START_FAILURE =
  "Couldn't start that support session - please try again.";
export const SAFE_SUPPORT_SESSION_END_FAILURE =
  "Couldn't end the support session - please try again.";

/** Client-side pre-flight refusals. Kept identical in wording to the server's own sentences. */
export const SUPPORT_SESSION_REASON_REQUIRED = "A reason is required to start a support session.";
export const SUPPORT_SESSION_REASON_TOO_SHORT = `Support session reason must be at least ${SUPPORT_SESSION_MIN_REASON_LENGTH} characters.`;

const KNOWN_SUPPORT_SESSION_FAILURES: ReadonlySet<string> = new Set([
  "Platform superadmin access is required to start a support session.",
  "Support session caller mismatch.",
  SUPPORT_SESSION_REASON_REQUIRED,
  SUPPORT_SESSION_REASON_TOO_SHORT,
  "Support session reason must be 500 characters or fewer.",
  "Support session reason cannot contain token or secret material.",
  "Support session length must be between 1 and 60 minutes.",
  "Membership was not found for this account.",
  "Only an active membership can be supported.",
  "Authentication is required to end a support session.",
  "Authentication is required to read a support session.",
]);

function mapKnownFailure(message: string, safe: string): string {
  return KNOWN_SUPPORT_SESSION_FAILURES.has(message) ? message : safe;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * The console's view of an open support session. Everything here is display material for the
 * banner plus the account the shell must pin — no reason body, and no secret material of any
 * kind, is carried through this type.
 */
export interface SupportSession {
  sessionId: string;
  targetAccountId: string;
  /** Null when the RPC projection omits it; the banner then drops the account clause rather than guessing. */
  targetAccountName: string | null;
  targetMembershipId: string;
  targetUserId: string | null;
  targetRole: string;
  targetDisplayName: string | null;
  startedAt: string | null;
  /** Null means "no clock returned" — treated as NOT active, so the UI fails closed. */
  expiresAt: string | null;
}

/** Accepts either a returns-table projection (array of rows) or a single json/record row. */
function firstRecord(data: unknown): Record<string, unknown> | null {
  if (Array.isArray(data)) {
    const row = data[0];
    return typeof row === "object" && row !== null ? (row as Record<string, unknown>) : null;
  }
  return typeof data === "object" && data !== null ? (data as Record<string, unknown>) : null;
}

/**
 * Map a session projection. Returns null unless the row carries the identifiers the console
 * needs to pin a workspace and name a target — a half-formed row is treated as "no session"
 * rather than rendered as a banner we cannot substantiate.
 */
export function mapSupportSessionRow(raw: Record<string, unknown>): SupportSession | null {
  const sessionId = asString(raw.session_id) ?? asString(raw.id);
  const targetAccountId = asString(raw.target_account_id) ?? asString(raw.account_id);
  const targetMembershipId = asString(raw.target_membership_id) ?? asString(raw.membership_id);
  if (sessionId === null || targetAccountId === null || targetMembershipId === null) {
    return null;
  }
  return {
    sessionId,
    targetAccountId,
    targetAccountName: asString(raw.target_account_name) ?? asString(raw.account_name),
    targetMembershipId,
    targetUserId: asString(raw.target_user_id),
    targetRole: asString(raw.target_role) ?? asString(raw.role) ?? "member",
    targetDisplayName:
      asString(raw.target_display_name) ??
      asString(raw.member_display_name) ??
      asString(raw.target_member_label),
    startedAt: asString(raw.started_at),
    expiresAt: asString(raw.expires_at),
  };
}

/**
 * True only while the session has a future expiry and no end stamp. The console re-checks this
 * on a timer so an expired session drops its banner without waiting for the next poll — the
 * server ends it either way, this just stops the UI claiming an elevation that is already gone.
 */
export function isSupportSessionActive(
  session: SupportSession | null,
  nowMs: number = Date.now(),
): session is SupportSession {
  if (session === null || session.expiresAt === null) {
    return false;
  }
  const expiresMs = new Date(session.expiresAt).getTime();
  return Number.isFinite(expiresMs) && expiresMs > nowMs;
}

/** Whole minutes remaining, floored at 0. Display only. */
export function supportSessionMinutesRemaining(
  session: SupportSession,
  nowMs: number = Date.now(),
): number {
  if (session.expiresAt === null) return 0;
  const expiresMs = new Date(session.expiresAt).getTime();
  if (!Number.isFinite(expiresMs)) return 0;
  return Math.max(0, Math.ceil((expiresMs - nowMs) / 60000));
}

/** Clamp a requested TTL into the locked 1–60 window; anything unparseable becomes the default. */
export function clampSupportTtlMinutes(value: unknown): number {
  const minutes = typeof value === "number" ? value : Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(minutes)) {
    return SUPPORT_SESSION_DEFAULT_TTL_MINUTES;
  }
  return Math.min(
    SUPPORT_SESSION_MAX_TTL_MINUTES,
    Math.max(SUPPORT_SESSION_MIN_TTL_MINUTES, Math.trunc(minutes)),
  );
}

/**
 * Read the actor's open session. Fails CLOSED to null: if the read errors, or returns a shape we
 * do not recognise, the console shows no banner. It never shows a banner it cannot justify, and
 * it never blocks the console on this read.
 */
export async function fetchMySupportSession(client: SupabaseClient): Promise<SupportSession | null> {
  try {
    const { data, error } = await client.rpc("my_platform_support_session");
    if (error !== null) {
      return null;
    }
    const record = firstRecord(data);
    if (record === null) {
      return null;
    }
    const session = mapSupportSessionRow(record);
    return isSupportSessionActive(session) ? session : null;
  } catch {
    return null;
  }
}

export type StartSupportSessionOutcome =
  | { ok: true; session: SupportSession | null }
  | { ok: false; reason: string };

export type EndSupportSessionOutcome = { ok: true } | { ok: false; reason: string };

export interface StartSupportSessionInput {
  accountId: string;
  membershipId: string;
  reason: string;
  ttlMinutes?: number;
}

/**
 * Start a support session as the given membership.
 *
 * `session` in the success outcome may be null when the RPC's safe projection does not carry
 * enough to name the target — the caller then re-reads my_platform_support_session() rather than
 * synthesising banner copy. Success is never assumed from a missing error alone: an RPC that
 * returns no error AND no recognisable row still counts as started (the server said so), but the
 * banner data comes from the follow-up read.
 */
export async function startSupportSession(
  client: SupabaseClient,
  input: StartSupportSessionInput,
): Promise<StartSupportSessionOutcome> {
  const reason = input.reason.trim();
  if (reason.length === 0) {
    return { ok: false, reason: SUPPORT_SESSION_REASON_REQUIRED };
  }
  if (reason.length < SUPPORT_SESSION_MIN_REASON_LENGTH) {
    return { ok: false, reason: SUPPORT_SESSION_REASON_TOO_SHORT };
  }
  try {
    const { data, error } = await client.rpc("start_platform_support_session", {
      p_account_id: input.accountId,
      p_membership_id: input.membershipId,
      p_reason: reason,
      p_ttl_minutes: clampSupportTtlMinutes(input.ttlMinutes ?? SUPPORT_SESSION_DEFAULT_TTL_MINUTES),
    });
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return { ok: false, reason: mapKnownFailure(message, SAFE_SUPPORT_SESSION_START_FAILURE) };
    }
    const record = firstRecord(data);
    return { ok: true, session: record === null ? null : mapSupportSessionRow(record) };
  } catch {
    return { ok: false, reason: SAFE_SUPPORT_SESSION_START_FAILURE };
  }
}

/** End the actor's open session. Idempotent server-side; a no-session call is still a success. */
export async function endSupportSession(client: SupabaseClient): Promise<EndSupportSessionOutcome> {
  try {
    const { error } = await client.rpc("end_platform_support_session");
    if (error !== null) {
      const message = typeof error.message === "string" ? error.message : "";
      return { ok: false, reason: mapKnownFailure(message, SAFE_SUPPORT_SESSION_END_FAILURE) };
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: SAFE_SUPPORT_SESSION_END_FAILURE };
  }
}
